import {
  Box,
  Card,
  CardActionArea,
  Link as MuiLink,
  Stack,
  Typography,
  type TypographyOwnProps,
  cardActionAreaClasses,
  styled,
  touchRippleClasses,
  useTheme,
} from "@mui/material"
import type { CSSProperties, PropsWithChildren, ReactNode } from "react"
import { ExternalLink, type Icon } from "react-feather"
import { Link } from "react-router-dom"
import type { IconUrl, IconUrlsProps } from "../../../icons"

type SummaryLinkCardProps = {
  // url?: string
  title: ReactNode
  subTitle?: string
  Icon: IconUrl | Icon
  displayBorder?: boolean
  configStyle?: SummaryLinkCardStyle
  variant?: "small" | "medium" | "large"
  isExternal?: boolean
  openInNewTab?: boolean
} & ({ to?: string; url?: never } | { to?: never; url?: string })

export type SummaryLinkCardStyle = {
  borderColor?: CSSProperties["color"]
  icon?: IconUrlsProps
} & SummaryContentLinkCardStyle

export interface SummaryContentLinkCardStyle {
  linkIconColor?: CSSProperties["color"]
  bgColor?: CSSProperties["color"]
  titleStyle?: Omit<TypographyOwnProps, "children">
  subTitleStyle?: Omit<TypographyOwnProps, "children">
}

export const SummaryLinkContainer = ({
  url,
  to,
  Icon,
  displayBorder = true,
  configStyle = {},
  variant = "small",
  openInNewTab = false,
  children,
}: PropsWithChildren<
  Omit<SummaryLinkCardProps, "isExternal" | "title" | "subTitle">
>) => {
  return (
    <Card
      sx={{
        width: "100%",
        height: "100%",
        borderColor: configStyle.borderColor,
        borderStyle: displayBorder ? undefined : "hidden",
        backgroundColor: configStyle.bgColor || "common.white",
      }}
    >
      <CardActionArea
        {...(to !== undefined
          ? {
              component: Link,
              to,
            }
          : url !== undefined
            ? {
                component: MuiLink,
                href: url,
                ...(openInNewTab && { target: "_blank", rel: "noreferrer" }),
              }
            : {
                component: Box,
              })}
        sx={{
          display: "flex",
          height: "100%",
          [`& > .${cardActionAreaClasses.focusHighlight},.${touchRippleClasses.child}`]:
            {
              bgcolor: configStyle.borderColor,
            },
        }}
      >
        <StackContainer variant={variant}>
          <Icon {...configStyle.icon} />
          {children}
        </StackContainer>
      </CardActionArea>
    </Card>
  )
}

type SummaryContentLinkCardProps = Omit<
  SummaryLinkCardProps,
  "Icon" | "displayBorder" | "configStyle" | "url"
> &
  SummaryContentLinkCardStyle

export const SummaryContentLinkCard = ({
  title,
  subTitle,
  variant,
  linkIconColor,
  isExternal,
  bgColor = "common.white",
  titleStyle = {},
  subTitleStyle = {},
}: SummaryContentLinkCardProps) => {
  return (
    <Stack gap={variantSize(variant, 1.25, 2, 3)} sx={{ backgroundColor: bgColor }}>
      <Typography
        variant="22px"
        fontWeight={600}
        color="common.darkBlue"
        {...titleStyle}
      >
        {title}
      </Typography>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
        <Typography
          variant="20px"
          fontWeight={500}
          color="common.darkBlue"
          width="70%"
          {...subTitleStyle}
        >
          {subTitle}
        </Typography>
        {isExternal && <ExternalLink size="18px" color={linkIconColor} />}
      </Stack>
    </Stack>
  )
}

export const SummaryLinkCard = ({
  url,
  title,
  subTitle,
  Icon,
  displayBorder = true,
  configStyle = {},
  variant = "small",
  isExternal = true,
  openInNewTab = false,
}: SummaryLinkCardProps) => {
  return (
    <SummaryLinkContainer
      url={url}
      Icon={Icon}
      displayBorder={displayBorder}
      configStyle={configStyle}
      variant={variant}
      openInNewTab={openInNewTab}
    >
      <SummaryContentLinkCard
        title={title}
        subTitle={subTitle}
        isExternal={isExternal}
        {...configStyle}
      />
    </SummaryLinkContainer>
  )
}

const variantSize = (
  variant: SummaryLinkCardProps["variant"],
  small: number,
  medium: number,
  large: number,
) => {
  const { spacing } = useTheme()
  if (!variant) return spacing(small)
  const size = variant === "small" ? small : variant === "medium" ? medium : large
  return spacing(size)
}

const StackContainer = styled(Stack, {
  shouldForwardProp: (props) => props !== "variant",
})<{ variant: SummaryLinkCardProps["variant"] }>(({ theme, variant }) => ({
  flex: 1,
  height: "100%",
  gap: variantSize(variant, 1.25, 2, 3),
  paddingInline: variantSize(variant, 3, 4, 5),
  paddingTop: variantSize(variant, 2, 3, 4),
  paddingBottom: variantSize(variant, 3, 4, 5),
}))
